/**
 * Matéria — a raiz da árvore de categorias (RN-37, RN-38).
 *
 * **Não existe entidade separada.** Matéria é o primeiro nível da árvore de
 * categorias da trilha, com rótulo próprio na interface. Uma tabela `materia`
 * ao lado de `categoria` criaria duas árvores para manter em sincronia e um
 * caso novo — o item cuja matéria não bate com a raiz da categoria dele — que
 * não teria como ser resolvido sem escolher um dos dois lados.
 *
 * O que a RN-38 acrescenta é que ela é a **unidade de agrupamento**: navegação
 * por categoria, páginas do caderno, cotas do modelo de prova e dosagem da meta
 * agrupam por matéria. Abaixo dela a profundidade continua livre.
 *
 * Por isso estas funções operam sobre o **caminho** — o array de nomes que já
 * viaja em todo lugar do contrato — e não sobre id. Quem tem o caminho tem a
 * matéria, sem uma segunda consulta.
 */
/** O rótulo do primeiro nível, para a interface não dizer "categoria" nele. */
export const ROTULO_DA_MATERIA = "Matéria";
/**
 * A matéria de um caminho: o primeiro nome.
 *
 * Caminho vazio devolve `null`, e não uma string de aviso. Item sem categoria
 * não deveria existir (RN-03), e devolver "Sem matéria" aqui faria a tela
 * mostrar um agrupamento com nome plausível em cima de um defeito de dado.
 */
export function materiaDe(caminho) {
    return caminho.length > 0 ? caminho[0] : null;
}
/**
 * O que vem **depois** da matéria, que é o que distingue duas linhas dentro do
 * mesmo grupo. Numa lista já agrupada por matéria, repetir a matéria em cada
 * linha é ruído.
 */
export function abaixoDaMateria(caminho) {
    return caminho.slice(1);
}
/** O assunto de um caminho: o segundo nível, quando existe (RN-46 agrupa por ele). */
export function assuntoDe(caminho) {
    return caminho.length > 1 ? caminho[1] : null;
}
/**
 * Agrupa por matéria **preservando a ordem de chegada** (RN-38).
 *
 * A ordem de dentro é a da lista original, e a ordem dos grupos é a da primeira
 * aparição de cada matéria. Reordenar aqui apagaria a ordenação que a lista já
 * carrega — o score de reincidência da fila de erradas, por exemplo, que custou
 * uma média bayesiana para ser calculado.
 */
export function agruparPorMateria(itens, caminhoDe) {
    const grupos = [];
    const porNome = new Map();
    for (const item of itens) {
        const materia = materiaDe(caminhoDe(item));
        // `null` e a string "null" precisam ser chaves diferentes: uma matéria
        // chamada "null" é improvável, mas colidir em silêncio é pior do que feio.
        const chave = materia === null ? " sem-materia" : materia;
        let grupo = porNome.get(chave);
        if (!grupo) {
            grupo = { materia, itens: [] };
            porNome.set(chave, grupo);
            grupos.push(grupo);
        }
        grupo.itens.push(item);
    }
    return grupos;
}
/** Agrupa em dois níveis: matéria e, dentro dela, assunto (RN-46). */
export function agruparPorMateriaEAssunto(itens, caminhoDe) {
    return agruparPorMateria(itens, caminhoDe).map((g) => ({
        materia: g.materia,
        itens: agruparPorMateria(g.itens, (i) => {
            // O assunto passa a ser a raiz do subagrupamento. Sem o `slice`, o
            // segundo `agruparPorMateria` reagruparia pela matéria de novo e
            // devolveria um grupo só.
            const c = caminhoDe(i);
            return c.slice(1);
        }),
    }));
}
/**
 * As matérias de uma trilha, na ordem em que aparecem, sem repetir.
 *
 * É o que alimenta um seletor de matéria — e ele não sai de uma consulta nova:
 * a lista de categorias com caminho já está em memória em toda tela que
 * precisa disso.
 */
export function materiasDe(caminhos) {
    const vistas = [];
    for (const c of caminhos) {
        const m = materiaDe(c);
        if (m !== null && !vistas.includes(m))
            vistas.push(m);
    }
    return vistas;
}
/**
 * O caminho está **dentro** da pasta? — a pergunta que a árvore do caderno faz
 * ao filtrar (RN-57).
 *
 * Dentro é a própria pasta **e tudo abaixo dela**: escolher "Modelagem" mostra
 * as páginas de "Modelagem › Normalização" junto. Filtrar por igualdade exata
 * faria a matéria parecer vazia sempre que as páginas morassem nos assuntos
 * dela, que é exatamente onde a RN-43 as põe.
 *
 * A comparação é por **nome de nível**, não por id, pelo mesmo motivo do resto
 * deste arquivo: o caminho é o que já viaja no contrato, dos dois lados, e quem
 * o tem não precisa de uma segunda consulta.
 *
 * Pasta vazia é a raiz: **tudo** está dentro dela. É o estado "nenhuma pasta
 * escolhida", e devolver `false` aqui esvaziaria a tela em vez de mostrá-la
 * inteira.
 */
export function dentroDe(pasta, caminho) {
    if (caminho.length < pasta.length)
        return false;
    return pasta.every((nivel, i) => caminho[i] === nivel);
}
//# sourceMappingURL=materia.js.map